import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const RefundPolicy = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1a0f0f] to-[#2d1810] py-20">
      <div className="container mx-auto px-6 max-w-4xl">
        {/* Back Button */}
        <button
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-orange-500 hover:text-orange-400 mb-8 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          Back to Home
        </button>

        {/* Content */}
        <div className="divine-card p-8 md:p-12">
          <h1 className="font-serif text-4xl md:text-5xl font-bold text-white mb-4">
            Refund Policy for ShraddhaAI
          </h1>
          <p className="text-gray-400 mb-8"><strong>Last Updated:</strong> December 4, 2025</p>

          <div className="space-y-6 text-gray-300 leading-relaxed">
            <p>
              ShraddhaAI is operated by <strong>Srivartin Corporation</strong>. We provide digital spiritual services, subscriptions, and virtual currency within the app. Please read this Refund Policy carefully before making any purchases.
            </p>

            <section>
              <h2 className="font-serif text-2xl font-bold text-white mb-4">1. Finality of Purchases</h2>
              <p className="mb-3">
                All purchases made within ShraddhaAI are <strong>final and non-refundable</strong>, except where required by applicable law.
              </p>

              <p className="mb-2">This includes:</p>
              <ul className="list-disc list-inside space-y-2 ml-4 mb-4">
                <li>Monthly or recurring subscriptions (₹149 / ₹499 plans)</li>
                <li>Shraddha Coin purchases</li>
                <li>Deity unlock purchases</li>
                <li>Any in-app digital features or upgrades</li>
              </ul>

              <p className="mb-2">By making a purchase, you acknowledge and agree that:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Digital services begin immediately upon activation.</li>
                <li>Virtual currency (Shraddha Coins) and unlocked content are considered consumed once credited to your account.</li>
                <li>We do not provide refunds or credits for partially used subscription periods.</li>
                <li>You waive any statutory "cooling-off" period or right of withdrawal to the maximum extent permitted under applicable law.</li>
              </ul>
            </section>

            <section>
              <h2 className="font-serif text-2xl font-bold text-white mb-4">2. Nature of Digital Services</h2>
              <p className="mb-3">
                ShraddhaAI provides AI-generated spiritual guidance and devotional experiences.
              </p>
              <p>
                Once access is granted to:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4 mb-3">
                <li>Voice call minutes</li>
                <li>Unlimited chat</li>
                <li>Premium deity unlocks</li>
                <li>Subscription benefits</li>
              </ul>
              <p>
                The service is deemed to have commenced and is not eligible for cancellation-based refund.
              </p>
            </section>

            <section>
              <h2 className="font-serif text-2xl font-bold text-white mb-4">3. Legal Exceptions</h2>
              <p className="mb-3">
                Refunds may be granted only if required under applicable consumer protection laws in your jurisdiction.
              </p>
              <p className="mb-2">If such a situation applies, users must contact us with:</p>
              <ul className="list-disc list-inside space-y-2 ml-4 mb-3">
                <li>Proof of purchase</li>
                <li>Transaction ID</li>
                <li>Description of issue</li>
              </ul>
              <p>
                Each case will be reviewed individually in accordance with applicable laws.
              </p>
            </section>

            <section>
              <h2 className="font-serif text-2xl font-bold text-white mb-4">4. Subscription Management</h2>
              <p className="mb-3">
                ShraddhaAI subscriptions are managed through direct in-app payment integrations.
              </p>
              <p className="mb-2"><strong>Important:</strong></p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Subscriptions cannot be cancelled through Google Play Store or Apple App Store subscription centers if purchased via direct integration.</li>
                <li>Users must manage or cancel subscriptions within the ShraddhaAI app.</li>
                <li>Cancellation stops future billing but does not refund previous payments.</li>
              </ul>
            </section>

            <section>
              <h2 className="font-serif text-2xl font-bold text-white mb-4">5. Accidental Purchases</h2>
              <p className="mb-3">
                We encourage users to review purchase details carefully before confirming payment.
              </p>
              <p className="mb-2">ShraddhaAI is not responsible for:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Accidental purchases</li>
                <li>Purchases made by family members using your device</li>
                <li>Unintended coin usage after confirmation</li>
              </ul>
            </section>

            <section>
              <h2 className="font-serif text-2xl font-bold text-white mb-4">6. Technical Issues</h2>
              <p>
                If you experience technical issues affecting access to your subscription, please contact us immediately. We will make reasonable efforts to resolve technical errors but this does not automatically entitle users to refunds.
              </p>
            </section>

            <section>
              <h2 className="font-serif text-2xl font-bold text-white mb-4">7. Contact Information</h2>
              <p className="mb-3">For billing inquiries or support:</p>
              <div className="mt-4 p-4 bg-orange-500/10 border border-orange-500/20 rounded-lg">
                <p className="mb-2">📧 Email: <a href="mailto:work.shraddhaai@gmail.com" className="text-orange-500 hover:text-orange-400">work.shraddhaai@gmail.com</a></p>
                <p className="mt-4">Operated by:<br /><strong>Srivartin Corporation</strong></p>
              </div>
            </section>

            <div className="mt-8 p-4 bg-orange-500/5 border border-orange-500/10 rounded-lg">
              <p className="text-sm text-gray-400">
                ShraddhaAI reserves the right to update this Refund Policy at any time. Continued use of the app constitutes acceptance of this policy.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RefundPolicy;